import type { InternalAxiosRequestConfig } from 'axios';
import { resolveFixture } from './tutorialRoutes';

/**
 * O portão do modo tutorial.
 *
 * Enquanto o tutorial roda, NENHUMA requisição à API da Almada sai desta máquina.
 * Quem decide a resposta é o roteiro; o que o roteiro não conhece é barrado.
 *
 * Por que negar por padrão, e não deixar passar o que não está no roteiro: uma
 * requisição não prevista que passasse adiante poderia ser um POST — e criaria uma
 * ocorrência de verdade, em nome do parceiro, durante um treinamento. Esse é o pior
 * desfecho possível desta feature. Se faltar um roteiro, o tutorial mostra uma tela
 * vazia; isso é recuperável, um sinistro criado por engano não é.
 *
 * O portão vive dentro de `Http.create` de propósito: não existe instância axios única
 * no projeto — a fábrica é chamada dezenas de vezes, uma por serviço. Interceptar na
 * fábrica cobre todas de uma vez, e naturalmente ignora chamadas a terceiros (ViaCEP,
 * feriados), que não passam por ela e não têm como criar nada aqui.
 */

let active = false;

/** Falhas do portão, para o tutorial poder se encerrar em vez de prender a pessoa. */
let onFailure: ((reason: string) => void) | null = null;

export function isTutorialActive(): boolean {
	return active;
}

export function openTutorialGateway(handlers: { onFailure: (reason: string) => void }): void {
	active = true;
	onFailure = handlers.onFailure;
}

export function closeTutorialGateway(): void {
	active = false;
	onFailure = null;
}

/** Métodos que mudam estado. Nunca chegam à rede em modo tutorial. */
const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Instala o portão numa instância do axios.
 *
 * Funciona trocando o `adapter` da requisição: o adapter é quem de fato fala com a
 * rede, então devolver um adapter sintético garante que nada sai — diferente de
 * rejeitar no interceptor, que dispararia o toast vermelho de erro de rede que o
 * projeto já mostra, assustando quem está aprendendo.
 */
export function installTutorialGateway(config: InternalAxiosRequestConfig) {
	if (!active) return config;

	const method = (config.method ?? 'get').toLowerCase();
	const url = config.url ?? '';
	const fixture = resolveFixture(method, url, config.params);

	if (fixture) {
		config.adapter = async () => ({
			data: fixture,
			status: 200,
			statusText: 'OK (tutorial)',
			headers: {},
			config,
		});

		return config;
	}

	if (MUTATING.has(method)) {
		// Escrita fora do roteiro é o caso que justifica o portão inteiro. Barra,
		// avisa alto no console e encerra o tutorial — seguir adiante com a pessoa
		// achando que gravou algo seria pior do que interromper.
		console.error('[tutorial] escrita bloqueada, fora do roteiro:', method.toUpperCase(), url);
		onFailure?.(`Uma ação não prevista foi bloqueada (${method.toUpperCase()} ${url}).`);
	} else {
		console.warn('[tutorial] leitura sem roteiro, respondida vazia:', method.toUpperCase(), url);
	}

	// Resposta inócua: a tela renderiza vazia em vez de quebrar, e nada saiu daqui.
	config.adapter = async () => ({
		data: { data: null },
		status: 200,
		statusText: 'OK (tutorial, sem roteiro)',
		headers: {},
		config,
	});

	return config;
}
